import { useEffect, useRef, useState } from "react";
import { Download, Upload } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { buttonClass } from "@/components/buttonStyles";
import {
  FILE_ICON_BUTTON,
  FILE_ROW,
  FileTile,
  PendingFileRow,
} from "@/components/FileRows";
import { QuoteChipBadge } from "@/components/submissions/QuoteCard";
import { downloadAccountDocument } from "@/lib/brokerAccounts";
import { acceptAttribute } from "@/lib/brokerDocumentTypes";
import { localTodayIso } from "@/lib/dates";
import { formatIsoDate } from "@/lib/displayFormat";
import { saveFile } from "@/lib/downloadFile";
import { messageFor } from "@/lib/errors";
import { rulesLine } from "@/lib/fileUploads";
import { formatShortDate } from "@/lib/listFormat";
import type { QuoteChip } from "@/lib/submissions/quoteCards";
import {
  answerable,
  answerSubjectivity,
  isOverdue,
  PARTY_LABELS,
  SUBJECTIVITY_STATUS_LABELS,
  subjectivityGroups,
  type QuoteSubjectivity,
  type SubjectivityStatus,
} from "@/lib/submissions/quoteConditions";
import { useDocumentTypes } from "@/lib/useDocumentTypes";
import { useFileUploads } from "@/lib/useFileUploads";

// What a quote is subject to (PMS-S749, BRK-S362), in its popup: before
// binding, then after, each with who acts, when it's due (overdue while still
// open past it), where it stands, and the files that answer it. One the broker
// or the insured acts on takes files until Signal closes it, one a request
// (`useFileUploads`) under PMS's rules for a quote (`?for=quote`); PMS's answer
// is what shows (Submitted), and the quotes are read again.

/** Each status as its chip shows it. */
const STATUS_TONES: Record<SubjectivityStatus, QuoteChip["tone"]> = {
  open: "draft",
  submitted: "in_progress",
  satisfied: "quoted",
  waived: "draft",
  not_met: "declined",
};

export function QuoteSubjectivities({
  submissionId,
  quoteId,
  accountId,
  subjectivities,
  onChanged,
}: {
  submissionId: string;
  quoteId: string;
  /** The insured's account, whose documents serve the downloads. */
  accountId: string | null;
  subjectivities: QuoteSubjectivity[];
  /** Reads the quotes again, once PMS has taken an answer. */
  onChanged: () => void | Promise<void>;
}) {
  const { authedRequest } = useAuth();
  const types = useDocumentTypes(authedRequest, true, "quote");
  // PMS's answers this visit, until the quotes are read again.
  const [answered, setAnswered] = useState<Record<string, QuoteSubjectivity>>(
    {},
  );
  useEffect(() => setAnswered({}), [subjectivities]);
  const shown = subjectivities.map((s) => answered[s.id] ?? s);
  const { beforeBinding, afterBinding } = subjectivityGroups(shown);
  const today = localTodayIso();

  const row = (subjectivity: QuoteSubjectivity) => (
    <SubjectivityRow
      key={subjectivity.id}
      subjectivity={subjectivity}
      overdue={isOverdue(subjectivity, today)}
      at={{ submissionId, quoteId, subjectivityId: subjectivity.id }}
      accountId={accountId}
      rules={types.rules}
      onAnswered={(next) => {
        setAnswered((all) => ({ ...all, [next.id]: next }));
        void onChanged();
      }}
    />
  );
  return (
    <div className="flex flex-col gap-4">
      <p className="text-brk-dim">
        Conditions this quote is subject to. Signal’s underwriters close each
        one.
      </p>
      {[
        ["Before binding", beforeBinding],
        ["After binding", afterBinding],
      ].map(([title, group]) =>
        typeof title === "string" &&
        Array.isArray(group) &&
        group.length > 0 ? (
          <div key={title} className="flex flex-col gap-2">
            <h4 className="font-semibold text-brk-text">{title}</h4>
            <ul className="flex flex-col gap-2">{group.map(row)}</ul>
          </div>
        ) : null,
      )}
    </div>
  );
}

function SubjectivityRow({
  subjectivity,
  overdue,
  at,
  accountId,
  rules,
  onAnswered,
}: {
  subjectivity: QuoteSubjectivity;
  overdue: boolean;
  at: { submissionId: string; quoteId: string; subjectivityId: string };
  accountId: string | null;
  rules: ReturnType<typeof useDocumentTypes>["rules"];
  onAnswered: (subjectivity: QuoteSubjectivity) => void;
}) {
  const { authedBlob, authedUploadWithProgress } = useAuth();
  const input = useRef<HTMLInputElement>(null);
  const [downloadProblem, setDownloadProblem] = useState<string | null>(null);
  const { uploads, send, stop, retry, dismiss } = useFileUploads(
    (file, options) =>
      answerSubjectivity(authedUploadWithProgress, at, { file, ...options }),
    onAnswered,
  );
  const {
    description,
    responsibleParty,
    dueDate,
    status,
    statusReason,
    documents,
  } = subjectivity;
  const closedWithReason =
    (status === "waived" || status === "not_met") && statusReason;

  async function download(documentId: string, filename: string) {
    if (!accountId) return;
    setDownloadProblem(null);
    try {
      saveFile(
        await downloadAccountDocument(authedBlob, accountId, documentId),
        filename,
      );
    } catch (err) {
      setDownloadProblem(`Didn’t download: ${messageFor(err)}`);
    }
  }

  return (
    <li className="flex flex-col gap-2 rounded-lg border border-brk-border p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="min-w-0 flex-1 break-words font-medium text-brk-text">
          {description}
        </p>
        <QuoteChipBadge
          chip={{
            label: SUBJECTIVITY_STATUS_LABELS[status],
            tone: STATUS_TONES[status],
          }}
        />
      </div>
      <p className="text-meta text-brk-dim">
        {PARTY_LABELS[responsibleParty]}
        {dueDate && ` · Due ${formatIsoDate(dueDate)}`}
        {overdue && (
          <span className="font-medium text-brk-danger"> · Overdue</span>
        )}
      </p>
      {closedWithReason && (
        <p className="text-meta text-brk-text">{statusReason}</p>
      )}
      {(documents.length > 0 || uploads.length > 0) && (
        <ul className="flex flex-col gap-2">
          {documents.map((d) => (
            <li key={d.documentId} className={`${FILE_ROW} border-brk-border`}>
              <FileTile filename={d.filename} tone="file" />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate font-semibold" title={d.filename}>
                  {d.filename}
                </span>
                <span className="text-meta text-brk-dim">
                  Added {formatShortDate(d.uploadedAt)}
                </span>
              </span>
              {accountId && (
                <button
                  type="button"
                  aria-label={`Download ${d.filename}`}
                  onClick={() => void download(d.documentId, d.filename)}
                  className={FILE_ICON_BUTTON}
                >
                  <Download aria-hidden="true" className="h-4 w-4" />
                </button>
              )}
            </li>
          ))}
          {uploads.map((u) => (
            <PendingFileRow
              key={u.key}
              upload={u}
              onStop={() => stop(u.key)}
              onRetry={() => retry(u.key)}
              onDismiss={() => dismiss(u.key)}
            />
          ))}
        </ul>
      )}
      {downloadProblem && (
        <p role="alert" className="text-meta text-brk-danger">
          {downloadProblem}
        </p>
      )}
      {answerable(subjectivity) && rules && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <input
            ref={input}
            type="file"
            multiple
            accept={acceptAttribute(rules)}
            aria-label={`Choose files for ${description}`}
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              // So the same file can be picked again after a refusal.
              e.target.value = "";
              if (files.length > 0) send(files, rules, documents.length);
            }}
          />
          <button
            type="button"
            onClick={() => input.current?.click()}
            className={buttonClass({ variant: "secondary", size: "sm" })}
          >
            <Upload aria-hidden="true" className="h-3.5 w-3.5" />
            Upload files
          </button>
          <span className="text-caption text-brk-dim">{rulesLine(rules)}</span>
        </div>
      )}
    </li>
  );
}
